import { ChangeDetectionStrategy, Component, computed, input, OnDestroy, output, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MarkdownComponent } from 'ngx-markdown';
import { FeedbackRating, FloMessage } from '../../../model/flo';
import { messageTime, messageTimestampFull } from './flo-time.util';
import { buildCopyText, citationToPlain, CITATION_LINE } from './flo-copy.util';

@Component({
	selector: 'app-flo-message',
	standalone: true,
	imports: [FormsModule, MarkdownComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	template: `
		<div class="flo-msg-row" [class.flo-msg-row--user]="message().role === 'user'"
		     [class.flo-msg-row--assistant]="message().role === 'assistant'">
			<div class="flo-msg" [class.flo-msg--user]="message().role === 'user'"
			     [class.flo-msg--assistant]="message().role === 'assistant'"
			     [class.flo-msg--error]="message().error">
				@if (message().role === 'assistant') {
					<markdown [data]="body()"></markdown>
					@if (offersReportDownload()) {
						<button class="flo-msg__download-report" type="button" (click)="downloadReport.emit()"
						        [disabled]="downloadingReport()" [attr.aria-busy]="downloadingReport()"
						        aria-label="Baixar relatório da empresa em PDF">
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
							     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
								<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
								<polyline points="7 10 12 15 17 10"/>
								<line x1="12" y1="15" x2="12" y2="3"/>
							</svg>
							{{ downloadingReport() ? 'Gerando relatório…' : 'Baixar relatório (PDF)' }}
						</button>
					}
					@if (sources().length) {
						<div class="flo-msg__citations">
							<span class="flo-msg__citations-title">Fontes</span>
							@for (s of sources(); track s) {
								<div class="flo-msg__citation">{{ s }}</div>
							}
						</div>
					}
					@if (message().error) {
						<button class="flo-msg__retry" type="button" (click)="retry.emit()">
							Tentar novamente
						</button>
					}
					@if (!message().streaming && !message().error && body()) {
						<div class="flo-msg__actions">
							<button class="flo-msg__action" type="button" (click)="copy()"
							        [attr.aria-label]="copied() ? 'Mensagem copiada' : 'Copiar mensagem'"
							        [attr.data-tip]="copied() ? 'Copiado!' : 'Copiar'">
								@if (!copied()) {
									<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
									     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
										<rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
										<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
									</svg>
								} @else {
									<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
									     stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
										<polyline points="20 6 9 17 4 12"/>
									</svg>
								}
							</button>
							<span class="flo-msg__sep" aria-hidden="true"></span>
							<button class="flo-msg__action flo-msg__action--icon" type="button" (click)="rate(Rating.Up)"
							        [class.flo-msg__action--on]="message().feedback === Rating.Up"
							        aria-label="Resposta útil" data-tip="Resposta útil">
								<svg width="14" height="14" viewBox="0 0 24 24"
								     [attr.fill]="message().feedback === Rating.Up ? 'currentColor' : 'none'"
								     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
									<path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
								</svg>
							</button>
							<button class="flo-msg__action flo-msg__action--icon" type="button" (click)="rate(Rating.Down)"
							        [class.flo-msg__action--on]="message().feedback === Rating.Down"
							        aria-label="Resposta não ajudou" data-tip="Resposta não ajudou">
								<svg width="14" height="14" viewBox="0 0 24 24"
								     [attr.fill]="message().feedback === Rating.Down ? 'currentColor' : 'none'"
								     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
									<path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
								</svg>
							</button>
						</div>
					}
					@if (commentOpen()) {
						<div class="flo-msg__comment">
							<textarea [(ngModel)]="commentDraft" rows="2" maxlength="500"
							          placeholder="O que faltou nesta resposta? (opcional)"
							          (keydown.control.enter)="submitComment()" (keydown.meta.enter)="submitComment()"></textarea>
							<div class="flo-msg__comment-actions">
								<button type="button" class="flo-msg__comment-btn" (click)="skipComment()">Pular</button>
								<button type="button" class="flo-msg__comment-btn flo-msg__comment-btn--primary"
								        (click)="submitComment()">Enviar</button>
							</div>
						</div>
					}
				} @else {
					<span class="flo-msg__text">{{ message().content }}</span>
				}
			</div>
			@if (timeLabel()) {
				<time class="flo-msg__time" [title]="timeTitle()">{{ timeLabel() }}</time>
			}
		</div>
	`,
	styleUrls: ['./flo-message.component.scss']
})
export class FloMessageComponent implements OnDestroy {
	readonly message = input.required<FloMessage>();
	readonly retry = output<void>();
	/** Emitted when the user clicks the report-download chip (the model offered the action). */
	readonly downloadReport = output<void>();
	/** true while the report PDF is being generated: the chip is disabled so it cannot open a second tab. */
	readonly downloadingReport = input(false);
	/** Emitted when the user rates this message; `rating: null` means they un-toggled it. */
	readonly feedback = output<{ rating: FeedbackRating | null; comment?: string }>();

	/** Exposes the enum to the template (Angular templates can't see module-level symbols). */
	protected readonly Rating = FeedbackRating;

	/** true while the (optional) "what was missing?" box is shown after a thumbs-down. */
	readonly commentOpen = signal(false);
	commentDraft = '';

	readonly offersReportDownload = computed(() =>
		(this.message().actions ?? []).includes('download-report'));

	readonly body = computed(() =>
		this.message().content.split('\n').filter(l => !CITATION_LINE.test(l)).join('\n'));
	readonly citations = computed(() => [...new Set(
		this.message().content.split('\n').filter(l => CITATION_LINE.test(l)).map(l => l.trim()))]);
	/** Citations cleaned for display via the shared {@link citationToPlain} (drops the "↳"/"[Fonte:]"
	 *  wrapper and collapses an "X · X" repeat) — the same cleaner the copy action uses, so the
	 *  rendered Fontes footer and the copied text never diverge. */
	readonly sources = computed(() => this.citations().map(citationToPlain).filter(Boolean));

	readonly timeLabel = computed(() => messageTime(this.message().createdAt));
	readonly timeTitle = computed(() => messageTimestampFull(this.message().createdAt));

	/** true for ~2s after a successful copy, swapping the icon/label to the "Copiado" state. */
	readonly copied = signal(false);
	private resetTimer: ReturnType<typeof setTimeout> | null = null;

	/** The clipboard payload: plain-text body + a "Fontes:" block (when the message has citations). */
	copyText(): string {
		return buildCopyText(this.body(), this.citations());
	}

	/** Copies {@link copyText} to the clipboard (with a non-secure-context fallback) and flashes "Copiado". */
	async copy(): Promise<void> {
		const text = this.copyText();
		let ok = false;
		try {
			if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(text);
				ok = true;
			}
		} catch { /* fall through to the execCommand fallback */ }
		if (!ok && typeof document !== 'undefined') {
			ok = this.fallbackCopy(text);
		}
		if (ok) {
			this.copied.set(true);
			if (this.resetTimer) clearTimeout(this.resetTimer);
			this.resetTimer = setTimeout(() => this.copied.set(false), 2000);
		}
	}

	/**
	 * Toggles the thumbs rating. Re-clicking the active thumb clears it; a fresh thumbs-down also
	 * opens the optional comment box. The rating itself is persisted immediately (even on a down with
	 * no comment) — the box is a chance to add detail, not a gate.
	 */
	rate(r: FeedbackRating): void {
		if (this.message().feedback === r) {
			this.commentOpen.set(false);
			this.feedback.emit({ rating: null });
			return;
		}
		this.feedback.emit({ rating: r });
		if (r === FeedbackRating.Down) {
			this.commentDraft = '';
			this.commentOpen.set(true);
		} else {
			this.commentOpen.set(false);
		}
	}

	/** Sends the typed reason alongside the (already-recorded) thumbs-down and closes the box. */
	submitComment(): void {
		const comment = this.commentDraft.trim();
		this.feedback.emit({ rating: FeedbackRating.Down, comment: comment || undefined });
		this.commentOpen.set(false);
	}

	/** Dismisses the comment box, leaving the thumbs-down recorded without a reason. */
	skipComment(): void {
		this.commentOpen.set(false);
	}

	ngOnDestroy(): void {
		if (this.resetTimer) clearTimeout(this.resetTimer);
	}

	/** Clipboard fallback for browsers/contexts without the async Clipboard API (e.g. non-HTTPS). */
	private fallbackCopy(text: string): boolean {
		const ta = document.createElement('textarea');
		ta.value = text;
		ta.style.position = 'fixed';
		ta.style.opacity = '0';
		document.body.appendChild(ta);
		ta.select();
		let ok = false;
		try { ok = document.execCommand('copy'); } catch { ok = false; }
		document.body.removeChild(ta);
		return ok;
	}
}
